import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import Button from "../../../../shared/ui/button/Button";
import { openSession, signIn } from "../../application/signIn";
import { motion } from "framer-motion";
import { Mail, ShieldCheck, AlertCircle, Eye, EyeOff } from "lucide-react";
import MfaChallenge from "./MfaChallenge";
import GoogleSignInButton from "./GoogleSignInButton";

export default function SignInForm() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [mfaData, setMfaData] = useState<{ email: string; password: string } | null>(null);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);

    const form = e.target as HTMLFormElement;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement).value;

    try {
      // 1. Backend sign-in
      const data = { email, password };
      const response = await signIn(data);

      if (response.status === "MFA_REQUIRED") {
        setMfaData({ email, password });
        return;
      }

      // 2. Firebase login (if no MFA), made this tab's current user
      await openSession(email, password);
      await new Promise((r) => setTimeout(r, 100));

      navigate("/");
    } catch (err: unknown) {
      console.error("Error during sign in:", err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any;

      if (error?.response?.status === 403 && error?.response?.data?.detail === "email_not_verified") {
        // The backend gate fires before the Firebase sign-in above, so without
        // this the user has no session — and /verify-email cannot resend
        // anything without one. Firebase allows sign-in while unverified.
        try {
          await openSession(email, password);
          navigate("/verify-email");
          return;
        } catch {
          setErrorMessage(t("auth.errorEmailNotVerified"));
        }
      } else if (error?.response?.status === 401 || error?.code === "auth/invalid-credential") {
        setErrorMessage(t("auth.errorInvalid"));
      } else {
        setErrorMessage(t("auth.errorGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" as const }
    }
  };

  return (
    <div className="w-full">
      {mfaData && (
        <MfaChallenge
          email={mfaData.email}
          password={mfaData.password}
          onCancel={() => setMfaData(null)}
        />
      )}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-8"
      >
        <motion.div variants={itemVariants}>
          <h1 className="text-3xl font-bold text-content-primary mb-2">
            {t("auth.welcomeTitle")}
          </h1>
          <p className="text-content-tertiary">
            {t("auth.welcomeSubtitle")}
          </p>
        </motion.div>

        <form onSubmit={handleSignIn}>
          <motion.div variants={containerVariants} className="space-y-6">
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-3 items-center"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {errorMessage}
              </motion.div>
            )}

            <motion.div variants={itemVariants} className="space-y-2">
              <Label htmlFor="email">
                {t("auth.email")} <span className="text-error-500">*</span>
              </Label>
              <div className="relative" suppressHydrationWarning>
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  className="pl-10"
                  placeholder={t("auth.enterEmail")}
                  required
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  {t("auth.password")} <span className="text-error-500">*</span>
                </Label>
                <Link
                  to="/reset-password"
                  className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <div className="relative" suppressHydrationWarning>
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="pl-10 pr-12"
                  placeholder={t("auth.enterPassword")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-content-tertiary hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
                >
                  {showPassword ? <Eye className="w-5 h-5 text-content-tertiary" /> : <EyeOff className="w-5 h-5 text-content-tertiary" />}
                </button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-4">
              <Button
                type="submit"
                className="w-full"
                loading={loading}
              >
                {t("auth.signIn")}
              </Button>
            </motion.div>
          </motion.div>
        </form>

        <motion.div variants={itemVariants} className="space-y-4">
          <div className="flex items-center gap-3">
            <hr className="flex-1 border-gray-200 dark:border-white/[0.08]" />
            <span className="text-xs text-content-tertiary font-medium">{t("common.or", "or")}</span>
            <hr className="flex-1 border-gray-200 dark:border-white/[0.08]" />
          </div>
          <GoogleSignInButton mode="signIn" />
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="pt-4 border-t border-gray-100 dark:border-white/[0.05] space-y-3"
        >
          <p className="text-sm font-medium text-center text-content-tertiary">
            {t("auth.noAccount")}{" "}
            <Link
              to="/signup"
              className="text-brand-600 dark:text-brand-400 font-bold hover:underline ml-1"
            >
              {t("auth.signUp")}
            </Link>
          </p>
          <p className="text-sm font-medium text-center text-content-tertiary">
            {t("auth.isPatient")}{" "}
            <Link
              to="/portal/login"
              className="text-brand-600 dark:text-brand-400 font-bold hover:underline ml-1"
            >
              {t("auth.goToPatientPortal")}
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
